/**
 * CheckoutActions — issue #25 (Becky-T1.2); money path fixed in #251.
 *
 * Two-button checkout pair. BOTH tracks mark departure AND create a
 * server-of-record OpenInvoice row (the money record — issue #251: no
 * checked-out booking may exit the money funnel silently). They differ
 * only in WHEN the money is collected:
 *
 *   - "Check Out & Settle Now" — opens the SettleNowDialog immediately:
 *     charge the saved card on file (#248 real charge endpoint) or record
 *     a desk payment (cash/external) via the invoice settle endpoint,
 *     which mints the real Payment ledger row. Dismissing the dialog
 *     degrades safely to settle-later (the invoice stays queued).
 *   - "Check Out — Settle Later" — leaves the invoice in the end-of-day
 *     queue for batch settlement on the Payments page.
 *
 * Direct fix for Ted/Julian's stated preference, confirmed by Becky during
 * the 2026-05-11 on-site walkthrough:
 *
 *   "Staff check the dog out (mark departure) without necessarily
 *    processing payment immediately. Payments batched at end of day."
 *
 * Decouples the operational reality (dog leaves) from the financial
 * reality (money recorded) without ever dropping the financial record.
 * Customers with an active pre-paid package have a day redeemed at
 * check-out on EITHER track (exactly once); the invoice is then written
 * at $0 with a "covered by package" note and settled immediately.
 *
 * For grooming bookings the row keeps a single "Check Out — Grooming"
 * button that opens TierConfirmation; the two-track choice happens inside
 * the modal's footer (see `TierConfirmation.tsx`). Daycare / boarding
 * surfaces the pair inline because there's no tier prompt to wrap it in.
 */

import type { ReactNode } from 'react';
import './CheckoutActions.css';

export type CheckoutAction = 'settle-now' | 'settle-later';

export interface CheckoutActionsProps {
  /** Disable both buttons while parent is processing. */
  readonly disabled?: boolean;
  /** Optional ARIA group label. */
  readonly ariaLabel?: string;
  /** Click handler — receives the chosen track. */
  readonly onChoose: (action: CheckoutAction) => void;
  /** Extra control rendered alongside (e.g. cancel). */
  readonly trailing?: ReactNode;
  /** When set, scope-test ids by booking id. */
  readonly testIdSuffix?: string;
  /** Override copy if the surface needs different language (e.g. inside modal). */
  readonly settleNowLabel?: string;
  readonly settleLaterLabel?: string;
}

const DEFAULT_SETTLE_NOW = 'Check Out & Settle Now';
const DEFAULT_SETTLE_LATER = 'Check Out — Settle Later';

export function CheckoutActions({
  disabled = false,
  ariaLabel = 'Checkout actions',
  onChoose,
  trailing,
  testIdSuffix,
  settleNowLabel = DEFAULT_SETTLE_NOW,
  settleLaterLabel = DEFAULT_SETTLE_LATER,
}: CheckoutActionsProps): JSX.Element {
  const suffix = testIdSuffix ? `-${testIdSuffix}` : '';
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="ferro-checkout-actions"
      data-testid={`checkout-actions${suffix}`}
    >
      {trailing}
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost"
        disabled={disabled}
        onClick={() => onChoose('settle-later')}
        data-testid={`checkout-settle-later${suffix}`}
      >
        {settleLaterLabel}
      </button>
      <button
        type="button"
        className="ferro-btn ferro-btn--primary"
        disabled={disabled}
        onClick={() => onChoose('settle-now')}
        data-testid={`checkout-settle-now${suffix}`}
      >
        {settleNowLabel}
      </button>
    </div>
  );
}
